import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Sun, Sunrise, Sunset, Moon, CloudSun } from 'lucide-react';

const PrayerTimes = () => {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextPrayer, setNextPrayer] = useState(null);

  // Barrie, Ontario coordinates
  const BARRIE_LAT = 44.3894;
  const BARRIE_LNG = -79.6903;

  useEffect(() => {
    const fetchPrayerTimes = async () => {
      try {
        const today = new Date();
        const date = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        
        const response = await fetch(
          `https://api.aladhan.com/v1/timings/${date}?latitude=${BARRIE_LAT}&longitude=${BARRIE_LNG}&method=2&school=1`
        );
        const data = await response.json();
        
        if (data.code === 200) {
          setPrayerTimes(data.data.timings);
        }
      } catch (error) {
        console.error('Error fetching prayer times:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrayerTimes();
    
    // Update current time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Calculate next prayer
  useEffect(() => {
    if (!prayerTimes) return;

    const prayers = [
      { name: 'Fajr', time: prayerTimes.Fajr },
      { name: 'Sunrise', time: prayerTimes.Sunrise },
      { name: 'Dhuhr', time: prayerTimes.Dhuhr },
      { name: 'Asr', time: prayerTimes.Asr },
      { name: 'Maghrib', time: prayerTimes.Maghrib },
      { name: 'Isha', time: prayerTimes.Isha },
    ];

    const now = currentTime;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    for (const prayer of prayers) {
      const [hours, minutes] = prayer.time.split(':').map(Number);
      const prayerMinutes = hours * 60 + minutes;
      
      if (prayerMinutes > currentMinutes) {
        setNextPrayer(prayer);
        return;
      }
    }
    
    // If all prayers have passed, next is Fajr tomorrow
    setNextPrayer({ name: 'Fajr', time: prayerTimes.Fajr, tomorrow: true });
  }, [prayerTimes, currentTime]);

  const formatTime = (time24) => {
    if (!time24) return '--:--';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getTimeUntilNext = () => {
    if (!nextPrayer || !prayerTimes) return '';
    
    const now = currentTime;
    const [hours, minutes] = nextPrayer.time.split(':').map(Number);
    
    let prayerDate = new Date(now);
    prayerDate.setHours(hours, minutes, 0, 0);
    
    if (nextPrayer.tomorrow) {
      prayerDate.setDate(prayerDate.getDate() + 1);
    }
    
    const diff = prayerDate - now;
    const hoursLeft = Math.floor(diff / (1000 * 60 * 60));
    const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hoursLeft > 0) {
      return `${hoursLeft}h ${minutesLeft}m`;
    }
    return `${minutesLeft}m`;
  };

  const prayerList = [
    { name: 'Fajr', icon: Sunrise, key: 'Fajr' },
    { name: 'Sunrise', icon: Sun, key: 'Sunrise' },
    { name: 'Dhuhr', icon: CloudSun, key: 'Dhuhr' },
    { name: 'Asr', icon: Sun, key: 'Asr' },
    { name: 'Maghrib', icon: Sunset, key: 'Maghrib' },
    { name: 'Isha', icon: Moon, key: 'Isha' },
  ];

  if (loading) {
    return (
      <div className="p-6 rounded-2xl bg-card border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="p-6 rounded-2xl bg-gradient-to-br from-card to-card/50 border border-border"
      data-testid="prayer-times"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-primary/10">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground font-['Outfit']">Prayer Times</h3>
            <p className="text-xs text-muted-foreground">Barrie, Ontario</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Next Prayer Highlight */}
      {nextPrayer && (
        <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-primary font-medium uppercase tracking-wider">Next Prayer</p>
              <p className="text-2xl font-bold text-foreground font-['Outfit']">{nextPrayer.name}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary font-mono">{formatTime(nextPrayer.time)}</p>
              <p className="text-xs text-muted-foreground">in {getTimeUntilNext()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Prayer Times Grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {prayerList.map((prayer) => {
          const Icon = prayer.icon;
          const isNext = nextPrayer?.name === prayer.name;
          const time = prayerTimes?.[prayer.key];
          
          return (
            <div
              key={prayer.name}
              className={`p-3 rounded-xl text-center transition-all ${
                isNext 
                  ? 'bg-primary/20 border border-primary/30' 
                  : 'bg-background/50 border border-border hover:border-primary/30'
              }`}
            >
              <Icon className={`h-4 w-4 mx-auto mb-1 ${isNext ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className={`text-xs font-medium ${isNext ? 'text-primary' : 'text-muted-foreground'}`}>
                {prayer.name}
              </p>
              <p className={`text-sm font-mono font-semibold ${isNext ? 'text-foreground' : 'text-foreground/80'}`}>
                {formatTime(time)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground mt-4">
        Times calculated for Barrie, ON • Hanafi method
      </p>
    </motion.div>
  );
};

export default PrayerTimes;
